// CPI API Response Types

// Response from POST /cpi/calculate
export interface CpiCalculateResponse {
  success: boolean;
  result: {
    ordersProcessed: number;
    shipmentsProcessed: number;
  };
}

// Response from GET /cpi/dashboard
export interface CpiDashboardData {
  startDate: string;
  endDate: string;
  lastCalculatedAt: string | null;

  businessOrderCount: number;
  shipmentCount: number;
  totalWeightTons: number;
  totalLoadPerformanceTonKm: number;

  totalEmissionWtwKg: number;
  totalEmissionTtwKg: number;
  totalEmissionWttKg: number;

  totalEmissionWtwTons: number;
  totalEmissionTtwTons: number;
  totalEmissionWttTons: number;

  avgCpiWtw: number | null;
  avgCpiTtw: number | null;
  avgCpiWtt: number | null;

  co2ePerTonWtw: number | null;
  co2ePerTonTtw: number | null;
  co2ePerTonWtt: number | null;

  co2PricePerTon: number;
  indicativeCostEuros: number;

  transportEmissions: {
    wtwKg: number;
    ttwKg: number;
    wttKg: number;
  };

  hubEmissions: {
    wtwKg: number | null;
    ttwKg: number | null;
    wttKg: number | null;
  };

  emissionSourceBreakdown: {
    FUEL: number;
    CPI: number;
    MODALITY: number;
    NONE: number;
  };
}

// Emission source types
export type EmissionSource = 'FUEL' | 'CPI' | 'MODALITY' | 'NONE';

// Trip-level CPI data
export interface CpiTrip {
  transportOrderNumber: string;
  orderNumber: string;
  volgnummer: string;
  executionDate: string;
  vehicleId: string;
  carrierId: string;
  clientId: string;
  toc: string | null;
  weightTons: number;
  distanceKm: number;
  loadPerformanceTonKm: number;
  // emissionWtwKg etc. are totals (transport + hub combined). The Hub variants
  // break out the HOC contribution; null when the trip has no HOC, the HOC's
  // factor wasn't computed, or the trip's date falls outside the HOC's period.
  emissionWtwKg: number;
  emissionWttKg: number;
  emissionTtwKg: number;
  emissionWtwHubKg: number | null;
  emissionTtwHubKg: number | null;
  emissionWttHubKg: number | null;
  hocId: string | null;
  cpiWtw: number | null; // kg CO2e per ton·km
  emissionSource: EmissionSource;
  indicativeCostEuros: number;
}

// Response from GET /cpi/trips
export interface CpiTripsResponse {
  trips: CpiTrip[];
  totalCount: number;
  limit: number;
  offset: number;
}

// Query parameters for GET /cpi/trips
export interface CpiTripsQueryParams {
  startDate: string; // YYYY-MM-DD (required)
  endDate: string; // YYYY-MM-DD (required)
  vehicleId?: string;
  carrierId?: string;
  clientId?: string;
  emissionSource?: EmissionSource;
  transportOrderNumber?: string;
  limit?: number;
  offset?: number;
}

// Tab type for CPI Reports page
/** Emissions sub-tab display mode: charts, or the raw trip list. */
export type EmissionsView = 'report' | 'list';

// Sub-tab type for Dashboard charts
export type DashboardSubTab = 'emissions' | 'operational' | 'energy';

// Ingestion Issues types

// Flat view: individual error row
export interface IngestionIssue {
  orderNumber: string;
  volgnummer: string | null;
  errorReason: string;
  errorCategory?: IngestionErrorType;
  errorDetails: string;
  sourceFilename: string;
  uploadDate: string;
}

// Grouped view: file-level summary
export interface IngestionFileGroup {
  sourceFilename: string;
  uploadDate: string;
  totalOrders: number;
  totalShipments: number;
  failedOrders: number;
  failureRate: number;
  calculationSkippedOrders: number | null;
  topErrors: { error: string; errorCategory?: IngestionErrorType; count: number }[];
}

// Paginated response wrapper
export interface IngestionIssuesPaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Error category enum values (must match backend enum exactly)
export const INGESTION_ERROR_TYPES = [
  'missing_address',
  'unknown_address',
  'invalid_country_code',
  'negative_weight',
  'missing_weight',
  'missing_emission_source',
  'duplicate',
  'duplicate_address',
  'no_address',
  'zero_weight',
  // 'no_vehicle_match', // Hidden — uncomment to re-enable vehicle match filter
  'no_load_carrier_match',
  'other',
] as const;

export type IngestionErrorType = (typeof INGESTION_ERROR_TYPES)[number];

// Error Distribution types
export interface ErrorDistributionCategory {
  errorCategory: string;
  count: number;
  percentage: number;
}

export interface ErrorDistributionResponse {
  totalErrors: number;
  categories: ErrorDistributionCategory[];
}

// Emission Source Distribution types
export interface EmissionSourceDistributionSource {
  emissionSource: EmissionSource;
  wtwKg: number;
  percentage: number;
}

export interface EmissionSourceDistributionResponse {
  totalWtwKg: number;
  sources: EmissionSourceDistributionSource[];
  dateRange: { startDate: string; endDate: string };
}

// --- Analytics Chart Response Types ---

// Graph 1 & 5: Emissions/Shipments by Customer
export interface AnalyticsByCustomerItem {
  clientId: string;
  currentValue: number;
  previousValue: number | null;
}

export interface AnalyticsByCustomerResponse {
  startDate: string;
  endDate: string;
  compareWithPreviousYear: boolean;
  data: AnalyticsByCustomerItem[];
}

// Emissions by TOC (type of contract field). `toc` is the canonical lowercase key
// (internal identity only, never displayed); `label` is the customer's own
// typed spelling and is what the chart renders. `toc: 'Unknown'` is the
// sentinel for shipments with no TOC, not a real category.
export interface EmissionsByTocItem {
  toc: string;
  label: string;
  currentValue: number;
}

export interface EmissionsByTocResponse {
  startDate: string;
  endDate: string;
  // true when this tenant has too many distinct TOC values for TOC to exist
  // in the pre-aggregated data at all — `data` is then empty and the chart
  // cannot be drawn; render an explanatory notice instead.
  overCap: boolean;
  data: EmissionsByTocItem[];
}

// Graph 2: Emissions by Month
export interface EmissionsByMonthItem {
  month: string; // "YYYY-MM"
  currentEmissionsKg: number;
  previousEmissionsKg: number | null;
}

export interface EmissionsByMonthResponse {
  startDate: string;
  endDate: string;
  compareWithPreviousYear: boolean;
  data: EmissionsByMonthItem[];
}

// Graph 3: Emissions by Fuel
export interface EmissionsByFuelItem {
  month: string; // "YYYY-MM"
  fuelType: string;
  emissionsKg: number;
}

export interface EmissionsByFuelResponse {
  startDate: string;
  endDate: string;
  data: EmissionsByFuelItem[];
}

// Graph 4: Weight by Modality
export interface WeightByModalityItem {
  month: string; // "YYYY-MM"
  modality: string;
  weightTons: number;
}

export interface WeightByModalityResponse {
  startDate: string;
  endDate: string;
  data: WeightByModalityItem[];
}

// Graph 6: Fuel Consumption
export interface FuelConsumptionSummaryItem {
  fuelType: string;
  quantity: number;
  unit: string;
  wttEmissionsKg: number;
  ttwEmissionsKg: number;
  wtwEmissionsKg: number;
}

export interface FuelConsumptionMonthlyItem {
  month: string; // "YYYY-MM"
  fuelType: string;
  quantity: number;
}

/**
 * Fuel actually attributed to the current selection, per fuel type.
 *
 * `summary` above is a tank-level rollup: whole overlapping fuel periods, and advanced
 * filters only decide which VEHICLES qualify. This row instead scales each fuel record by
 * the share of its vehicle-period load performance that the CPI engine attributed inside
 * the selected range and filters, so its WTW total reconciles with the Reporting FUEL KPI.
 */
export interface FuelConsumptionAttributedItem {
  fuelType: string;
  quantity: number;
  unit: string;
  wttEmissionsKg: number;
  ttwEmissionsKg: number;
  wtwEmissionsKg: number;
  /** attributed / reported, as a percentage. Null when nothing was reported for this fuel. */
  sharePct: number | null;
}

export interface FuelConsumptionResponse {
  startDate: string;
  endDate: string;
  summary: FuelConsumptionSummaryItem[];
  monthly: FuelConsumptionMonthlyItem[];
  attributed: FuelConsumptionAttributedItem[];
}

// Graph 7: CO2e per ton (kg) WTW per month
export interface IntensityPerTonMonthlyItem {
  month: string; // "YYYY-MM"
  totalEmissionWtwKg: number;
  totalWeightTons: number;
  intensityKgPerTon: number;
}

export interface IntensityPerTonMonthlyResponse {
  startDate: string;
  endDate: string;
  data: IntensityPerTonMonthlyItem[];
}

// Graph 8: CO2e per ton-km (kg) WTW per month
export interface IntensityPerTonKmMonthlyItem {
  month: string; // "YYYY-MM"
  totalEmissionWtwKg: number;
  totalLoadPerfTonKm: number;
  intensityKgPerTonKm: number;
}

export interface IntensityPerTonKmMonthlyResponse {
  startDate: string;
  endDate: string;
  data: IntensityPerTonKmMonthlyItem[];
}

// Graph 9: CO2e per ton (kg) WTW per modality
export interface IntensityByModalityItem {
  modality: string;
  totalEmissionWtwKg: number;
  totalWeightTons: number;
  intensityKgPerTon: number;
}

export interface IntensityByModalityResponse {
  startDate: string;
  endDate: string;
  data: IntensityByModalityItem[];
}

// Graph 10: CO2e per ton-km (kg) WTW per vehicle (top 10)
export interface IntensityByVehicleItem {
  vehicleId: string;
  totalEmissionWtwKg: number;
  totalLoadPerfTonKm: number;
  intensityKgPerTonKm: number;
}

export interface IntensityByVehicleResponse {
  startDate: string;
  endDate: string;
  data: IntensityByVehicleItem[];
}

// Graph 11: CO2e per ton-km (kg) WTW per order (top 10)
export interface IntensityByOrderItem {
  transportOrderNumber: string;
  totalEmissionWtwKg: number;
  totalLoadPerfTonKm: number;
  intensityKgPerTonKm: number;
}

export interface IntensityByOrderResponse {
  startDate: string;
  endDate: string;
  data: IntensityByOrderItem[];
}

// Constants
export const DEFAULT_CO2_PRICE_PER_TON = 45.0; // EUR per ton CO2e

// Re-export number formatting utilities from shared module
export { formatNumber, formatCurrency, formatAuto } from '../../utils/formatNumber';

/**
 * Advanced filter selections for CPI dashboard.
 * All fields are string arrays supporting multi-select.
 */
export interface AdvancedFilters {
  carrierIds: string[];
  fuelTypes: string[];
  modalities: string[];
  emissionSources: string[];
  shipperIds: string[];
  /** Canonical TOC keys (lowercase), not display spellings. */
  tocs: string[];
}

export const EMPTY_ADVANCED_FILTERS: AdvancedFilters = {
  carrierIds: [],
  fuelTypes: [],
  modalities: [],
  emissionSources: [],
  shipperIds: [],
  tocs: [],
};

export const EMISSION_SOURCE_OPTIONS = ['FUEL', 'CPI', 'MODALITY', 'NONE'];

export interface DataQualityNoCpiReasons {
  zeroWeight: number;
  zeroDistance: number;
  noAddress: number;
  notCalculatedYet: number;
}

export interface DataQualityByClientItem {
  clientId: string;
  totalTrips: number;
  tripsWithoutCpi: number;
  dataQuality: number;
  reasons: DataQualityNoCpiReasons;
}

export interface DataQualityByClientResponse {
  startDate: string;
  endDate: string;
  data: DataQualityByClientItem[];
}
